// src/js/utils/serverSort.ts
// Phase 257 (257-02, D-01/D-02/D-03/D-04) — the client-side mirror of `App\Mc\ServerSort::compare()`
// (257-01, `app/Mc/ServerSort.php`).
//
// This module exists because `src/js/mc.ts` touches the DOM (querySelector, appendChild,
// col.style.order writes), so `node tests/mc/*.test.ts` could never load it — the same reason
// `src/js/utils/rosterSort.ts` (Phase 253) and `src/js/utils/feedTail.ts` (Phase 243) were split
// out of their own page-touching modules before it. `app/Mc/ServerSort.php` is this module's
// server-side twin: the two must be changed together, or the initial server-rendered order and the
// first 30s poll-tick reorder will disagree, reintroducing the exact one-tick jump this phase
// closes.
//
// One deviation from `rosterSort.ts`'s literal zero-import purity, noted explicitly: this module
// imports `wireInstantToUtcIso` from `./instants`, which is itself a zero-import module (see its
// own header docblock) — so `serverSort.ts` stays "near-zero-import" and remains fully
// `node`-loadable with no jsdom and no bundler. Nothing in this module may touch `document`,
// `window`, `fetch`, or a timer.

import { wireInstantToUtcIso } from './instants.ts';

/**
 * The four `ServerData` (`mc.ts`) fields `compareServers()` reads. A structural subset, not a
 * separate payload shape — `ServerData` already carries all four fields on the wire, so no payload
 * change is needed anywhere to use this comparator.
 */
export interface SortableServer {
    isOnline: boolean;
    onlinePlayers: number | null;
    lastSeenAt: string | null;
    name: string;
}

/**
 * Reinterprets a wire `lastSeenAt` string as a comparable epoch-ms timestamp, routing through
 * `wireInstantToUtcIso()` first (mandatory — see that function's own docblock for the scar: a raw
 * `new Date(server.lastSeenAt)` lands up to four hours off because `->format('c')` on a
 * Nette-hydrated DATETIME carries naive UTC digits under a bogus runtime offset). Returns `null`
 * for both the null-input case and the `isNaN` case (an unparseable string) — both degrade to
 * "never seen" in `compareServers()` below, never to a thrown error or an arbitrary `NaN`
 * comparison.
 */
function lastSeenTimestamp(lastSeenAt: string | null): number | null {
    const utc = wireInstantToUtcIso(lastSeenAt);
    if (utc === null) {
        return null;
    }
    const t = new Date(utc).getTime();
    return isNaN(t) ? null : t;
}

/**
 * The single ordering rule for the `/mc` fleet list, client side. Rule (checked in this order,
 * mirroring `App\Mc\ServerSort::compare()` exactly):
 *   1. D-01: an online server always sorts before an offline one, regardless of player count or
 *      timestamp on either side. `ServerStatus::online()` hardcodes `lastSeenAt: null` for online
 *      servers, so the null-last rule below must never be reached from this branch.
 *   2. D-01: both online — `(b.onlinePlayers ?? 0) - (a.onlinePlayers ?? 0)`. Equal player counts
 *      deliberately return `0` — a literal reading of D-01's "online servers keep today's ordering
 *      — unchanged" — and `Array.prototype.sort` is stable, so a `0` here means "keep what the
 *      server already decided," not "gain a new tiebreak."
 *   3. Offline group: last-seen descending, alphabetical tiebreak on an exact timestamp match
 *      (D-02), and a server that has never been seen online — `lastSeenAt` null or unparseable —
 *      sorts below every offline server that has a timestamp (D-03), "infinitely stale."
 *
 * Never throws. Every `lastSeenAt` comparison routes through `lastSeenTimestamp()` above, which
 * already handles both wire shapes and already returns `null` on garbage; this comparator treats
 * `null` as never-seen rather than re-parsing.
 */
export function compareServers(a: SortableServer, b: SortableServer): number {
    // D-01: online cluster first, unchanged player-count-desc ordering within it.
    if (a.isOnline !== b.isOnline) {
        return a.isOnline ? -1 : 1;
    }
    if (a.isOnline && b.isOnline) {
        return (b.onlinePlayers ?? 0) - (a.onlinePlayers ?? 0);
    }

    // Offline group: last-seen desc, alphabetical tiebreak, never-seen (null or unparseable)
    // sorts last.
    const aTs = lastSeenTimestamp(a.lastSeenAt);
    const bTs = lastSeenTimestamp(b.lastSeenAt);

    if (aTs === null && bTs === null) {
        return a.name.localeCompare(b.name); // D-02
    }
    if (aTs === null) {
        return 1; // D-03: never-seen is infinitely stale
    }
    if (bTs === null) {
        return -1;
    }

    return aTs !== bTs ? bTs - aTs : a.name.localeCompare(b.name); // descending, D-02 exact-tie tiebreak
}
